import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Alert, Form, Button, Collapse, Card, FilterGrid, FilterField } from '@/components/ui';
import reportService, { MaterialUsagePreviewData } from '../../../services/reportService';
import ReportParametersCard from '../components/ReportParametersCard';
import ReportPreviewTable from '../components/ReportPreviewTable';
import ExportOptionsModal from '../components/ExportOptionsModal';
import Toast from '../../../components/Toast';
import { MultiSelectDropdown } from '../../../components/MultiSelectDropdown';
import { daysAgoISO } from '../../../utils/dateUtils';
import { ServiceArea } from '../../../services/areasService';

interface MaterialUsageReportViewProps {
  serviceAreas: ServiceArea[];
  canCreateMaterialUsage: boolean;
  loadingServiceAreas: boolean;
  defaultServiceAreaId?: string;
}

const MaterialUsageReportView: React.FC<MaterialUsageReportViewProps> = ({ 
  serviceAreas, 
  canCreateMaterialUsage, 
  loadingServiceAreas,
  defaultServiceAreaId
}) => {
  const [filters, setFilters] = useState({
    serviceArea: defaultServiceAreaId || '',
    startDate: daysAgoISO(28),
    endDate: '',
    location: '',
    material: '',
    taskNumber: '',
    project: '',
    materialType: '',
    pickupLocation: ''
  });
  const [previewData, setPreviewData] = useState<MaterialUsagePreviewData[]>([]); // Current page data
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
    onPageChange: (page: number, pageSize?: number) => {} // Will be overridden
  });
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const [loadingFilterOptions, setLoadingFilterOptions] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string>('');
  const [showToast, setShowToast] = useState(false);
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(false);
  const [showAdditionalFilters, setShowAdditionalFilters] = useState(false);
  const [dateFilterType, setDateFilterType] = useState<'submission' | 'received'>('submission');
  const [filterOptions, setFilterOptions] = useState<{
    materialUsed: string[];
    taskNumber: string[];
    projectId: string[];
    pickupYard: string[];
    accomplishmentAssets: string[];
    materialType: string[];
  }>({
    materialUsed: [],
    taskNumber: [],
    projectId: [],
    pickupYard: [],
    accomplishmentAssets: [],
    materialType: []
  });
  const hasRunReportRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const showSuccessToast = (message: string) => {
    setToastMessage(message);
    setShowToast(true);
  };

  // Server-side pagination helper
  const handlePageChange = (page: number, pageSize?: number) => {
    handleGeneratePreview(page, pageSize);
  };

  // Handle page size changes
  const handlePageSizeChange = (newPageSize: number) => {
    handleGeneratePreview(1, newPageSize);
  };

  // Load filter options using DISTINCT queries (much more efficient)
  const loadFilterOptions = useCallback(async (signal?: AbortSignal) => {
    // Only load if we have at least a service area selected
    if (!filters.serviceArea) {
      setFilterOptions({
        materialUsed: [],
        taskNumber: [],
        projectId: [],
        pickupYard: [],
        accomplishmentAssets: [],
        materialType: []
      });
      return;
    }

    setLoadingFilterOptions(true);
    try {
      const options = await reportService.getMaterialUsageFilterOptions({
        serviceArea: filters.serviceArea,
        startDate: filters.startDate,
        endDate: filters.endDate,
        dateFilterType
      }, signal);

      // Store filter options directly only if not aborted
      if (!signal?.aborted) {
        setFilterOptions(options);
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'name' in error && error.name === 'AbortError') {
        return; // Ignore abort errors
      }
      console.error('Error loading filter options:', error);
      setFilterOptions({
        materialUsed: [],
        taskNumber: [],
        projectId: [],
        pickupYard: [],
        accomplishmentAssets: [],
        materialType: []
      });
    } finally {
      setLoadingFilterOptions(false);
    }
  }, [filters.serviceArea, filters.startDate, filters.endDate, dateFilterType]);

  // Auto-load filter options when service area or date range changes
  useEffect(() => {
    // Only auto-load if service area is selected and we haven't manually run the report yet
    if (filters.serviceArea && !hasRunReportRef.current) {
      const abortController = new AbortController();
      
      // Debounce the API call slightly to avoid too many requests
      const timeoutId = setTimeout(() => {
        loadFilterOptions(abortController.signal);
      }, 300);

      return () => {
        clearTimeout(timeoutId);
        abortController.abort();
      };
    }
  }, [filters.serviceArea, filters.startDate, filters.endDate, dateFilterType, loadFilterOptions]);

  const handleGeneratePreview = async (
    page: number = 1,
    pageSize?: number,
    sortFieldOverride?: string,
    sortDirectionOverride?: 'asc' | 'desc'
  ) => {
    // Cancel any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    
    setLoadingPreview(true);
    setErrorMessage(null);
    
    const effectiveSortField = sortFieldOverride !== undefined ? sortFieldOverride : sortField;
    const effectiveSortDirection = sortDirectionOverride !== undefined ? sortDirectionOverride : sortDirection;
    
    try {
      const response = await reportService.getMaterialUsagePreviewData({
        serviceArea: filters.serviceArea,
        startDate: filters.startDate,
        endDate: filters.endDate,
        dateFilterType,
        location: filters.location,
        material: filters.material,
        taskNumber: filters.taskNumber,
        project: filters.project,
        materialType: filters.materialType,
        pickupLocation: filters.pickupLocation,
        page,
        limit: pageSize || pagination.limit,
        sortField: effectiveSortField,
        sortDirection: effectiveSortDirection
      }, abortController.signal);

      // Store only current page data (server-side pagination) if not aborted
      if (!abortController.signal.aborted) {
        setPreviewData(response.data);
        hasRunReportRef.current = true; // Mark that report has been run
        
        setPagination({
          page: response.pagination.page,
          limit: response.pagination.limit,
          total: response.pagination.total,
          totalPages: response.pagination.totalPages,
          onPageChange: handlePageChange
        });
      }
      
    } catch (error) {
      if (error && typeof error === 'object' && 'name' in error && error.name === 'AbortError') {
        return; // Ignore abort errors
      }
      if (!abortController.signal.aborted) {
        setErrorMessage('Failed to generate material usage report preview. Please try again.');
        setPreviewData([]);
        setPagination({
          page: 1,
          limit: 50,
          total: 0,
          totalPages: 0,
          onPageChange: handlePageChange
        });
      }
    } finally {
      if (!abortController.signal.aborted) {
        setLoadingPreview(false);
      }
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null;
      }
    }
  };

  const handleSort = (field: string) => {
    const newDirection = field === sortField && sortDirection === 'desc' ? 'asc' : 'desc';
    setSortField(field);
    setSortDirection(newDirection);
    handleGeneratePreview(1, undefined, field, newDirection); // Use new sort immediately (avoids stale state)
  };

  const handleExport = async (customFilename?: string, reportType?: string) => {
    setLoadingExport(true);
    try {
      await reportService.generateMaterialUsageReport({
        serviceArea: filters.serviceArea,
        startDate: filters.startDate,
        endDate: filters.endDate,
        dateFilterType,
        location: filters.location,
        material: filters.material,
        taskNumber: filters.taskNumber,
        project: filters.project,
        materialType: filters.materialType,
        pickupLocation: filters.pickupLocation,
        customFilename,
        reportType
      });

      showSuccessToast('Material usage report exported successfully!');
      setShowExportModal(false);
      
    } catch (error) {
      setErrorMessage('Failed to export material usage report. Please try again.');
    } finally {
      setLoadingExport(false);
    }
  };

  // Generate default filename based on format: Material Usage - DATE
  const generateDefaultFilename = (reportType: 'simple' | 'sage-ready' = 'simple') => {
    let baseFilename = '';
    if (filters.endDate) {
      const date = new Date(filters.endDate);
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      baseFilename = `Material Usage_${year}_${month}_${day}`;
    } else {
      const date = new Date();
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      baseFilename = `Material Usage_${year}_${month}_${day}`;
    }
    
    const suffix = reportType === 'sage-ready' ? '_Sage' : '_Simple';
    return baseFilename + suffix;
  };

  const handleExportWithFilename = async (postedDate: string, customFilename?: string, reportType?: string) => {
    setLoadingExport(true);
    try {
      await reportService.generateMaterialUsageReport({
        serviceArea: filters.serviceArea,
        startDate: filters.startDate,
        endDate: filters.endDate,
        dateFilterType,
        location: filters.location,
        material: filters.material,
        taskNumber: filters.taskNumber,
        project: filters.project,
        materialType: filters.materialType,
        pickupLocation: filters.pickupLocation,
        customFilename,
        reportType
      });

      showSuccessToast('Material usage report exported successfully!');
      setShowExportModal(false);
      
    } catch (error) {
      setErrorMessage('Failed to export material usage report. Please try again.');
    } finally {
      setLoadingExport(false);
    }
  };

  // Extract unique values from filter options for dropdown filters
  const getUniqueValues = (key: keyof MaterialUsagePreviewData): string[] => {
    // Map MaterialUsagePreviewData keys to filterOptions keys
    switch (key) {
      case 'materialUsed':
        return filterOptions.materialUsed;
      case 'taskNumber':
        return filterOptions.taskNumber;
      case 'projectId':
        return filterOptions.projectId;
      case 'pickupYard':
        return filterOptions.pickupYard;
      case 'accomplishmentAssets':
        return filterOptions.accomplishmentAssets;
      case 'materialType':
        return filterOptions.materialType;
      default:
        // Fallback: return empty array if key not in filterOptions
        // Note: We can't extract from all data since we only have paginated previewData
        return [];
    }
  };

  const columns = [
    { 
      label: 'Employee Name', 
      key: 'employeeName',
      sortable: true,
      width: '150px'
    },
    { 
      label: 'Pickup Yard', 
      key: 'pickupYard',
      sortable: true,
      width: '130px'
    },
    { 
      label: 'Quantity', 
      key: 'quantity',
      sortable: true,
      width: '100px'
    },
    { 
      label: 'Unit of Measure', 
      key: 'unitOfMeasure',
      sortable: true,
      width: '140px'
    },
    { 
      label: 'Material Used', 
      key: 'materialUsed',
      sortable: true,
      width: '200px'
    },
    { 
      label: 'Task ID', 
      key: 'taskNumber',
      sortable: true,
      width: '120px'
    },
    { 
      label: 'Work Location', 
      key: 'accomplishmentAssets',
      sortable: true,
      width: '250px'
    },
    { 
      label: 'Project', 
      key: 'projectId',
      sortable: true,
      width: '120px'
    },
    { 
      label: 'Material Rate', 
      key: 'materialRate',
      sortable: true,
      width: '130px'
    },
    { 
      label: 'Total Cost', 
      key: 'totalCost',
      sortable: true,
      width: '120px',
      render: (row: MaterialUsagePreviewData) => 
        typeof row.totalCost === 'number' ? row.totalCost.toFixed(2) : row.totalCost
    },
    { 
      label: 'Vehicle', 
      key: 'vehicle',
      sortable: true,
      width: '120px'
    },
    { 
      label: 'Work Date', 
      key: 'date',
      sortable: true,
      width: '130px'
    },
    { 
      label: 'Activity Number', 
      key: 'activityNumber',
      sortable: false,
      width: '150px'
    },
    { 
      label: 'Job', 
      key: 'job',
      sortable: true,
      width: '120px'
    },
    { 
      label: 'Cost Code', 
      key: 'costCode',
      sortable: false,
      width: '130px'
    },
    { 
      label: 'Owner', 
      key: 'owner',
      sortable: true,
      width: '120px'
    }
  ];

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column'
    }}>
      {/* Header Section - Fixed */}
      <div style={{ flexShrink: 0 }}>
        {errorMessage && <Alert variant="danger" className="tw-mb-3">{errorMessage}</Alert>}
        
      <ReportParametersCard
        onGenerate={() => handleGeneratePreview()}
        onExport={() => {}}
        loadingGenerate={loadingPreview}
        loadingExport={false}
        disableExport={true}
        generateButtonText="Run Report"
        hideExportButton={true}
        defaultCollapsed={false}
        onCollapseChange={setIsFiltersCollapsed}
      >
        <FilterGrid min="lg">
          <Form.Group className="tw-mb-1">
            <Form.Label className="tw-text-sm tw-mb-1">Service Area</Form.Label>
            {/* Value stays a comma-separated id string, so every request below is unchanged;
                the server reads one id or a list. */}
            <MultiSelectDropdown
              options={serviceAreas}
              value={filters.serviceArea ? filters.serviceArea.split(',') : []}
              onChange={(selected) => {
                setFilters({ ...filters, serviceArea: selected.map(String).join(',') });
                // Reset report state when service area changes
                hasRunReportRef.current = false;
                setPreviewData([]);
                setPagination(prev => ({ ...prev, total: 0, totalPages: 0 }));
              }}
              formatOption={(area) => area.name}
              getValue={(area) => String(area.id)}
              placeholder="All Service Areas"
              disabled={loadingServiceAreas}
            />
          </Form.Group>
          <FilterField span={2}>
            <Form.Group className="tw-mb-1">
              <Form.Label className="tw-text-sm tw-mb-1">Date range</Form.Label>
              <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
                <div className="tw-flex tw-items-center tw-gap-2">
                  <span className="tw-text-content-muted tw-text-sm">Filter by:</span>
                  <Form.Check
                    type="radio"
                    id="dateFilter-submission"
                    name="dateFilterType"
                    label="Work date"
                    title="The date the work was done, as entered on the submission"
                    checked={dateFilterType === 'submission'}
                    onChange={() => {
                      setDateFilterType('submission');
                      hasRunReportRef.current = false;
                      setPreviewData([]);
                      setPagination(prev => ({ ...prev, total: 0, totalPages: 0 }));
                    }}
                    className="tw-text-sm"
                  />
                  <Form.Check
                    type="radio"
                    id="dateFilter-received"
                    name="dateFilterType"
                    label="Received in WRAP"
                    title="The date WRAP received the submission"
                    checked={dateFilterType === 'received'}
                    onChange={() => {
                      setDateFilterType('received');
                      hasRunReportRef.current = false;
                      setPreviewData([]);
                      setPagination(prev => ({ ...prev, total: 0, totalPages: 0 }));
                    }}
                    className="tw-text-sm"
                  />
                </div>
                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                  <Form.Control
                    size="sm"
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                    style={{ width: '140px' }}
                  />
                  <span className="tw-text-content-muted tw-text-sm">to</span>
                  <Form.Control
                    size="sm"
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                    style={{ width: '140px' }}
                  />
                </div>
              </div>
            </Form.Group>
          </FilterField>
        </FilterGrid>

        {/* Additional Filters - Collapsible */}
        <div className="tw-mt-3">
          <Button
            variant="link"
            size="sm"
            onClick={() => setShowAdditionalFilters(!showAdditionalFilters)}
            className="tw-p-0 tw-no-underline"
            style={{ fontSize: '0.875rem' }}
          >
            <i className={`bi bi-chevron-${showAdditionalFilters ? 'up' : 'down'} tw-mr-1`}></i>
            Additional Filters
          </Button>
          <Collapse in={showAdditionalFilters}>
            <div>
              <FilterGrid className="tw-mt-2">
                <Form.Group className="tw-mb-1">
                  <Form.Label className="tw-text-sm tw-mb-1" title="Where the material was used: road, structure or rest area">
                    Work Location
                  </Form.Label>
                  <Form.Select
                    size="sm"
                    value={filters.location}
                    onChange={(e) => setFilters({ ...filters, location: e.target.value })}
                    disabled={loadingFilterOptions}
                  >
                    <option value="">All Work Locations</option>
                    {loadingFilterOptions ? (
                      <option disabled>Loading...</option>
                    ) : (
                      getUniqueValues('accomplishmentAssets').map((location, idx) => (
                        <option key={idx} value={location}>
                          {location}
                        </option>
                      ))
                    )}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="tw-mb-1">
                  <Form.Label className="tw-text-sm tw-mb-1">Material</Form.Label>
                  <Form.Select
                    size="sm"
                    value={filters.material}
                    onChange={(e) => setFilters({ ...filters, material: e.target.value })}
                    disabled={loadingFilterOptions}
                  >
                    <option value="">All Materials</option>
                    {loadingFilterOptions ? (
                      <option disabled>Loading...</option>
                    ) : (
                      getUniqueValues('materialUsed').map((material, idx) => (
                        <option key={idx} value={material}>
                          {material}
                        </option>
                      ))
                    )}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="tw-mb-1">
                  <Form.Label className="tw-text-sm tw-mb-1">Task ID</Form.Label>
                  <Form.Select
                    size="sm"
                    value={filters.taskNumber}
                    onChange={(e) => setFilters({ ...filters, taskNumber: e.target.value })}
                    disabled={loadingFilterOptions}
                  >
                    <option value="">All Task IDs</option>
                    {loadingFilterOptions ? (
                      <option disabled>Loading...</option>
                    ) : (
                      getUniqueValues('taskNumber').map((taskNum, idx) => (
                        <option key={idx} value={taskNum}>
                          {taskNum}
                        </option>
                      ))
                    )}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="tw-mb-1">
                  <Form.Label className="tw-text-sm tw-mb-1">Project</Form.Label>
                  <Form.Select
                    size="sm"
                    value={filters.project}
                    onChange={(e) => setFilters({ ...filters, project: e.target.value })}
                    disabled={loadingFilterOptions}
                  >
                    <option value="">All Projects</option>
                    {loadingFilterOptions ? (
                      <option disabled>Loading...</option>
                    ) : (
                      getUniqueValues('projectId').map((projectId, idx) => (
                        <option key={idx} value={projectId}>
                          {projectId}
                        </option>
                      ))
                    )}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="tw-mb-1">
                  <Form.Label className="tw-text-sm tw-mb-1">Material Type</Form.Label>
                  <Form.Select
                    size="sm"
                    value={filters.materialType}
                    onChange={(e) => setFilters({ ...filters, materialType: e.target.value })}
                    disabled={loadingFilterOptions}
                  >
                    <option value="">All Material Types</option>
                    {loadingFilterOptions ? (
                      <option disabled>Loading...</option>
                    ) : (
                      getUniqueValues('materialType').map((materialType, idx) => (
                        <option key={idx} value={materialType}>
                          {materialType}
                        </option>
                      ))
                    )}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="tw-mb-1">
                  <Form.Label className="tw-text-sm tw-mb-1">Pickup Location</Form.Label>
                  <Form.Select
                    size="sm"
                    value={filters.pickupLocation}
                    onChange={(e) => setFilters({ ...filters, pickupLocation: e.target.value })}
                    disabled={loadingFilterOptions}
                  >
                    <option value="">All Pickup Locations</option>
                    {loadingFilterOptions ? (
                      <option disabled>Loading...</option>
                    ) : (
                      getUniqueValues('pickupYard').map((pickup, idx) => (
                        <option key={idx} value={pickup}>
                          {pickup}
                        </option>
                      ))
                    )}
                  </Form.Select>
                </Form.Group>
              </FilterGrid>
            </div>
          </Collapse>
        </div>
      </ReportParametersCard>
      </div>
      
      
      {/* Report Data Section - Flexible */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        minWidth: 0
      }}>
        <Card style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          flex: 1,
          minWidth: 0
        }}>
          <Card.Header className="tw-flex tw-justify-between tw-items-center" style={{ flexShrink: 0 }}>
          <h6 className="tw-mb-0">Report Data ({pagination.total} records)</h6>
          {pagination.total > 0 && (
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => setShowExportModal(true)}
              disabled={!canCreateMaterialUsage}
            >
              <i className="bi bi-download tw-mr-1"></i>
              Export Report
            </Button>
          )}
        </Card.Header>

          {/* Table Container - ReportPreviewTable owns its own scroll region (single mechanism,
              see ReportPreviewTable.tsx); no extra overflow wrapper needed here. */}
          <Card.Body className="tw-p-0" style={{
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column'
          }}>
          <ReportPreviewTable
            columns={columns}
            data={previewData}
            pagination={{
              ...pagination,
              onPageChange: handlePageChange,
              onPageSizeChange: handlePageSizeChange
            }}
            loading={loadingPreview}
            emptyMessage="No data available. Click 'Run Report' to load data."
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
            isFiltersCollapsed={isFiltersCollapsed}
          />
          </Card.Body>
        </Card>
      </div>

      <ExportOptionsModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onPreliminaryExport={handleExport}
        onFinalExport={handleExportWithFilename}
        loadingPreliminary={loadingExport}
        loadingFinal={loadingExport}
        isMaterialUsage={true}
        showFilenameInput={true}
        generateMaterialUsageFilename={generateDefaultFilename}
      />

      <Toast
        show={showToast}
        onClose={() => setShowToast(false)}
        message={toastMessage}
        type="success"
      />
    </div>
  );
};

export default MaterialUsageReportView;

